import React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import Tag from "../../ui-components/molecules/tag/tag";
import { TagColor } from "../../app/data/common/tagColor";
import { ReactComponent as EditIcon } from "../../images/edit_blue.svg";
import binIcon from "../../images/bin_gray.svg";

const columnHelper = createColumnHelper<any>();

const getSubTypeLabel = (subType: string | undefined | null): string => {
  if (!subType) return "-";
  
  const subTypeMap: Record<string, string> = {
    LOADING_DOOR: "Loading Door",
    UNLOADING_DOOR: "Unloading Door",
    CITY_DELIVERY_DOOR: "City Delivery Door",
    STORAGE_BIN: "Storage Bin",
    STAGING_AREA: "Staging Area",
    TEMPORARY_AREA: "Temporary Area",
  };
  
  return subTypeMap[subType] || subType;
};

const GetLocationMasterColumns = (onEdit?: (location: any) => void, onDelete?: (location: any) => void) => {
  return [
    columnHelper.accessor("locationCode", {
      id: "locationCode",
      header: "Location Code",
      enableColumnFilter: false,
      size: 80,
    }),
    columnHelper.accessor("locationType", {
      id: "locationType",
      header: "Location Type",
      enableColumnFilter: false,
      size: 100,
      cell: (cellProps) => (
        <Tag mods={{ color: TagColor.BLUE, uppercase: true }}>
          {cellProps.getValue()}
        </Tag>
      ),
    }),
    columnHelper.accessor("subType", {
      id: "subType",
      header: "Sub-Type",
      enableColumnFilter: false,
      size: 100,
      cell: (cellProps) => getSubTypeLabel(cellProps.getValue()),
    }),
    columnHelper.accessor("trailer", {
      id: "trailer",
      header: "Trailer",
      enableColumnFilter: false,
      size: 80,
    }),
    columnHelper.accessor("distributorName", {
      id: "distributorConsignee",
      header: "Distributor/Consignee",
      enableColumnFilter: false,
      size: 150,
      cell: (cellProps) => {
        const primaryDistributor = cellProps.getValue();
        const secondaryDistributors = cellProps.row.original.secondaryDistributors || [];
        
        if (!primaryDistributor && secondaryDistributors.length === 0) {
          return "-";
        }
        
        // If there are secondary distributors, show the count
        if (secondaryDistributors.length > 0) {
          const allDistributors = [
            primaryDistributor,
            ...secondaryDistributors.map((d: { distributorName: string }) => d.distributorName)
          ].filter(Boolean);
          
          return (
            <div title={allDistributors.join(", ")}>
              {primaryDistributor || secondaryDistributors[0]?.distributorName || "-"}
              {allDistributors.length > 1 && (
                <span style={{ 
                  marginLeft: "6px", 
                  fontSize: "11px", 
                  color: "#6b7280",
                  backgroundColor: "#e5e7eb",
                  padding: "2px 6px",
                  borderRadius: "10px"
                }}>
                  +{allDistributors.length - 1}
                </span>
              )}
            </div>
          );
        }
        
        return primaryDistributor || "-";
      },
    }),
    columnHelper.accessor("terminalName", {
      id: "terminalName",
      header: "Terminal Name",
      enableSorting: false,
      enableColumnFilter: false,
      size: 120,
    }),
    columnHelper.accessor("manifestNumber", {
      id: "manifestNumber",
      header: "Manifest #",
      enableColumnFilter: false,
      size: 100,
    }),
    columnHelper.accessor("percentFilled", {
      id: "percentFilled",
      header: "% Filled",
      enableColumnFilter: false,
      size: 70,
      cell: (cellProps) => `${cellProps.getValue()}%`,
    }),
    columnHelper.accessor("totalWeight", {
      id: "totalWeight",
      header: "Total Weight",
      enableColumnFilter: false,
      size: 100,
      cell: (cellProps) => `${cellProps.getValue().toLocaleString()} lbs`,
    }),
    columnHelper.accessor("state", {
      id: "state",
      header: "State",
      enableColumnFilter: false,
      size: 90,
      cell: (cellProps) => {
        const value = cellProps.getValue();
        let color = TagColor.GREY;
        let displayValue = value;
        
        // Map API lifecycle states to display values and colors
        if (value === "AVAILABLE") {
          color = TagColor.GREEN;
          displayValue = "Available";
        } else if (value === "ASSIGNED" || value === "LOADING" || value === "UNLOADING") {
          color = TagColor.BLUE;
          displayValue = value === "ASSIGNED" ? "Assigned" : value === "LOADING" ? "Loading" : "Unloading";
        } else if (value === "CLOSED" || value === "EMPTY") {
          color = TagColor.GREY;
          displayValue = value === "CLOSED" ? "Closed" : "Empty";
        } else if (value === "BLOCKED") {
          color = TagColor.ORANGE;
          displayValue = "Blocked";
        } else {
          // Fallback for legacy values
          if (value === "In Use") color = TagColor.BLUE;
          else if (value === "Reserved") color = TagColor.YELLOW;
          else if (value === "Maintenance") color = TagColor.ORANGE;
          displayValue = value;
        }
        
        return (
          <Tag mods={{ color: color, uppercase: true }}>
            {displayValue}
          </Tag>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "Actions",
      enableColumnFilter: false,
      size: 100,
      cell: (cellProps) => (
        <div className="xgs-location-master-summary__orders__iconcheck" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <EditIcon
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.(cellProps.row.original);
            }}
            style={{ cursor: "pointer" }}
          />
          <img
            src={binIcon}
            alt="Delete"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(cellProps.row.original);
            }}
            style={{ cursor: "pointer", width: "16px", height: "16px" }}
          />
        </div>
      ),
    }),
  ];
};
// Only sorting on "Location Code", "Location Type", and "Sub-Type"
export const LOCATION_MASTER_SORT_EXCLUDED_COLUMN_INDEXES = [
  3,
  4,
  5,
  6,
  7,
  // 8,
  // 9,
];

export default GetLocationMasterColumns;

